import styled, { css } from 'styled-components';
import type {
    CohortCellTone,
    DashboardStatIconKey,
    DataSourceBadgeVariant,
    InsightTone,
    MetricHintTone,
    OrganizationSignal,
    OrganizationStatus,
} from './types';

export const DashboardContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
`;

export const ScrollBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background: var(--color-muted, #f8fafc);
    padding: 20px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 28px;
`;

export const TabBar = styled.div`
    display: flex;
    align-items: stretch;
    flex-shrink: 0;
    border-bottom: 1px solid var(--color-border, #e5e7eb);
    background: var(--color-background, #fff);
`;

export const TabPanelFrame = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
`;

export const ScrollPeriod = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: var(--color-muted-foreground, #6b7280);
    white-space: nowrap;
`;

const dataSourceBadgeVariantStyles: Record<DataSourceBadgeVariant, ReturnType<typeof css>> = {
    live: css`
        background: var(--color-emerald-50, #ecfdf5);
        color: var(--color-emerald-700, #047857);
        border-color: var(--color-emerald-200, #a7f3d0);
    `,
    mock: css`
        background: var(--color-amber-50, #fffbeb);
        color: var(--color-amber-700, #b45309);
        border-color: var(--color-amber-200, #fde68a);
    `,
};

export const DataSourceBadge = styled.span<{ $variant: DataSourceBadgeVariant }>`
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid transparent;
    ${({ $variant }) => dataSourceBadgeVariantStyles[$variant]}
`;

export const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
`;

export const SectionEyebrow = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--color-muted-foreground, #6b7280);
`;

export const SectionTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--color-foreground, #111827);
`;

export const SectionHint = styled.p`
    margin: 0;
    font-size: 12px;
    color: var(--color-muted-foreground, #6b7280);
    line-height: 1.4;
`;

const insightToneStyles: Record<InsightTone, ReturnType<typeof css>> = {
    positive: css`
        background: var(--color-emerald-50, #ecfdf5);
        border-color: var(--color-emerald-200, #a7f3d0);
        color: var(--color-emerald-800, #065f46);
    `,
    warning: css`
        background: var(--color-amber-50, #fffbeb);
        border-color: var(--color-amber-200, #fde68a);
        color: var(--color-amber-800, #92400e);
    `,
    negative: css`
        background: var(--color-red-50, #fef2f2);
        border-color: var(--color-red-200, #fecaca);
        color: var(--color-red-800, #991b1b);
    `,
};

export const SectionInsightCallout = styled.p<{ $tone: InsightTone }>`
    margin: 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.45;
    ${({ $tone }) => insightToneStyles[$tone]}

    svg {
        width: 15px;
        height: 15px;
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

export const TabList = styled.div`
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    padding: 0 16px;
    min-width: 0;
`;

export const TabButton = styled.button<{ $isActive: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 500;
    border: none;
    background: none;
    cursor: pointer;
    white-space: nowrap;
    border-bottom: 2px solid
        ${({ $isActive }) => ($isActive ? 'var(--color-primary, #2563eb)' : 'transparent')};
    color: ${({ $isActive }) =>
        $isActive ? 'var(--color-primary, #2563eb)' : 'var(--color-muted-foreground, #6b7280)'};
    transition:
        color 0.15s ease,
        border-color 0.15s ease;

    svg {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
    }

    &:hover {
        color: ${({ $isActive }) =>
            $isActive ? 'var(--color-primary, #2563eb)' : 'var(--color-foreground, #111827)'};
    }
`;

export const TabBarMeta = styled.span`
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
`;

export const StatGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
`;

const dashboardStatIconToneStyles: Record<DashboardStatIconKey, ReturnType<typeof css>> = {
    mrr: css`
        background: #eff6ff;
        border-color: #bfdbfe;
        color: #2563eb;
    `,
    grossMargin: css`
        background: #ecfdf5;
        border-color: #a7f3d0;
        color: #059669;
    `,
    cost: css`
        background: #fef2f2;
        border-color: #fecaca;
        color: #dc2626;
    `,
    activeOrgs: css`
        background: #eef2ff;
        border-color: #c7d2fe;
        color: #4f46e5;
    `,
    nrr: css`
        background: #ecfdf5;
        border-color: #a7f3d0;
        color: #059669;
    `,
    losing: css`
        background: #fff7ed;
        border-color: #fed7aa;
        color: #ea580c;
    `,
    newRevenue: css`
        background: #eff6ff;
        border-color: #bfdbfe;
        color: #2563eb;
    `,
    expansionRevenue: css`
        background: #ecfdf5;
        border-color: #a7f3d0;
        color: #059669;
    `,
    contractionRevenue: css`
        background: #fffbeb;
        border-color: #fde68a;
        color: #b45309;
    `,
    churnedRevenue: css`
        background: #fef2f2;
        border-color: #fecaca;
        color: #dc2626;
    `,
    arpa: css`
        background: #f0fdfa;
        border-color: #99f6e4;
        color: #0d9488;
    `,
    momGrowth: css`
        background: #eff6ff;
        border-color: #bfdbfe;
        color: #2563eb;
    `,
    ltv: css`
        background: #f5f3ff;
        border-color: #ddd6fe;
        color: #7c3aed;
    `,
    cac: css`
        background: #f8fafc;
        border-color: #e2e8f0;
        color: #64748b;
    `,
    ltvCac: css`
        background: #ecfdf5;
        border-color: #a7f3d0;
        color: #059669;
    `,
    payback: css`
        background: #fff7ed;
        border-color: #fed7aa;
        color: #ea580c;
    `,
    grossMarginPercent: css`
        background: #ecfdf5;
        border-color: #a7f3d0;
        color: #059669;
    `,
    logoChurn: css`
        background: #f8fafc;
        border-color: #e2e8f0;
        color: #64748b;
    `,
    revenueChurn: css`
        background: #fef2f2;
        border-color: #fecaca;
        color: #dc2626;
    `,
    avgLifetime: css`
        background: #eef2ff;
        border-color: #c7d2fe;
        color: #4f46e5;
    `,
    activation: css`
        background: #f5f3ff;
        border-color: #ddd6fe;
        color: #7c3aed;
    `,
    timeToValue: css`
        background: #eff6ff;
        border-color: #bfdbfe;
        color: #2563eb;
    `,
    steps: css`
        background: #f0fdfa;
        border-color: #99f6e4;
        color: #0d9488;
    `,
    workflows: css`
        background: #eef2ff;
        border-color: #c7d2fe;
        color: #4f46e5;
    `,
    tokens: css`
        background: #f5f3ff;
        border-color: #ddd6fe;
        color: #7c3aed;
    `,
};

export const StatIcon = styled.span<{ $tone: DashboardStatIconKey }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    border: 1px solid transparent;

    svg {
        width: 12px;
        height: 12px;
    }

    ${({ $tone }) => dashboardStatIconToneStyles[$tone]}
`;

const hintToneColor: Record<MetricHintTone, string> = {
    positive: 'var(--color-emerald-600, #059669)',
    negative: 'var(--color-red-600, #dc2626)',
    neutral: 'var(--color-muted-foreground, #6b7280)',
};

export const Card = styled.div`
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: 12px;
    padding: 16px;
    background: var(--color-card, #fff);
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const CardHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

export const CardTitle = styled.h3`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-foreground, #111827);
`;

export const CardHint = styled.span`
    font-size: 12px;
    color: var(--color-muted-foreground, #6b7280);
`;

export const TwoColumn = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

export const RatioRows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const RatioRow = styled.div`
    display: grid;
    grid-template-columns: 56px 1fr 72px;
    align-items: center;
    gap: 10px;
`;

export const RatioLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: var(--color-muted-foreground, #6b7280);
`;

export const RatioTrack = styled.div`
    height: 14px;
    border-radius: 999px;
    background: var(--color-muted, #f1f5f9);
    overflow: hidden;
`;

export const RatioFill = styled.div<{ $percent: number; $variant: 'ltv' | 'cac' }>`
    height: 100%;
    width: ${({ $percent }) => `${Math.min(100, Math.max(0, $percent))}%`};
    border-radius: 999px;
    background: ${({ $variant }) =>
        $variant === 'ltv' ? 'var(--color-primary, #2563eb)' : 'var(--color-slate-400, #94a3b8)'};
`;

export const RatioValue = styled.span`
    font-size: 12px;
    font-weight: 700;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: var(--color-foreground, #111827);
`;

export const RatioCallout = styled.div`
    margin-top: 4px;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--color-emerald-50, #ecfdf5);
    color: var(--color-emerald-700, #047857);
    font-size: 13px;
    font-weight: 700;
`;

export const PaybackBig = styled.p`
    margin: 0;
    font-size: 40px;
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1;
    color: var(--color-foreground, #111827);

    span {
        font-size: 16px;
        font-weight: 600;
        color: var(--color-muted-foreground, #6b7280);
        margin-left: 6px;
    }
`;

export const PaybackNote = styled.p`
    margin: 0;
    font-size: 12px;
    color: var(--color-muted-foreground, #6b7280);
    max-width: 280px;
    line-height: 1.45;
`;

export const PaybackTicks = styled.div`
    display: flex;
    gap: 4px;
    margin-top: 4px;
`;

export const PaybackTick = styled.div<{ $isFilled: boolean }>`
    width: 22px;
    height: 8px;
    border-radius: 2px;
    background: ${({ $isFilled }) =>
        $isFilled ? 'var(--color-primary, #2563eb)' : 'var(--color-muted, #e2e8f0)'};
`;

export const CohortTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
`;

export const CohortTh = styled.th`
    padding: 8px 10px;
    text-align: center;
    border: 1px solid var(--color-border, #e5e7eb);
    background: var(--color-muted, #f8fafc);
    color: var(--color-muted-foreground, #6b7280);
    font-weight: 600;

    &:first-child {
        text-align: left;
    }
`;

export const CohortTd = styled.td`
    padding: 8px 10px;
    text-align: center;
    border: 1px solid var(--color-border, #e5e7eb);
    font-weight: 600;
    color: var(--color-foreground, #111827);

    &:first-child {
        text-align: left;
        white-space: nowrap;
    }
`;

const cohortToneBackground: Record<CohortCellTone, string> = {
    high: 'var(--color-emerald-100, #d1fae5)',
    mid: 'var(--color-amber-100, #fef3c7)',
    low: 'var(--color-red-100, #fee2e2)',
};

const cohortToneColor: Record<CohortCellTone, string> = {
    high: 'var(--color-emerald-800, #065f46)',
    mid: 'var(--color-amber-800, #92400e)',
    low: 'var(--color-red-800, #991b1b)',
};

export const CohortCell = styled.td<{ $tone: CohortCellTone }>`
    padding: 8px 10px;
    text-align: center;
    border: 1px solid var(--color-border, #e5e7eb);
    font-weight: 700;
    background: ${({ $tone }) => cohortToneBackground[$tone]};
    color: ${({ $tone }) => cohortToneColor[$tone]};
`;

export const TableCard = styled(Card)`
    padding: 0;
    overflow: hidden;
`;

export const TableCardHeader = styled.div`
    padding: 16px 16px 0;
`;

export const MarginTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

export const MarginTh = styled.th<{ $align?: 'left' | 'right' }>`
    text-align: ${({ $align = 'left' }) => $align};
    padding: 10px 14px;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-muted-foreground, #6b7280);
    border-bottom: 1px solid var(--color-border, #e5e7eb);
`;

export const MarginTd = styled.td<{ $align?: 'left' | 'right' }>`
    text-align: ${({ $align = 'left' }) => $align};
    padding: 10px 14px;
    border-top: 1px solid var(--color-border, #e5e7eb);
    font-size: 13px;
    color: var(--color-foreground, #111827);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const MarginFootTd = styled(MarginTd)`
    font-weight: 700;
    background: var(--color-muted, #f8fafc);
`;

export const SortButton = styled.button`
    appearance: none;
    border: none;
    background: transparent;
    padding: 0;
    font: inherit;
    font-weight: 600;
    color: var(--color-primary, #2563eb);
    cursor: pointer;
`;

export const OrgName = styled.div`
    font-weight: 600;
`;

export const OrgId = styled.div`
    font-size: 11px;
    color: var(--color-muted-foreground, #6b7280);
`;

const statusStyles: Record<OrganizationStatus, ReturnType<typeof css>> = {
    active: css`
        background: var(--color-emerald-50, #ecfdf5);
        color: var(--color-emerald-700, #047857);
    `,
    past_due: css`
        background: var(--color-amber-50, #fffbeb);
        color: var(--color-amber-700, #b45309);
    `,
    no_subscription: css`
        background: var(--color-muted, #f1f5f9);
        color: var(--color-muted-foreground, #64748b);
    `,
};

export const StatusBadge = styled.span<{ $status: OrganizationStatus }>`
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    ${({ $status }) => statusStyles[$status]}
`;

export const MarginAmount = styled.span<{ $isPositive: boolean }>`
    font-weight: 700;
    color: ${({ $isPositive }) =>
        $isPositive ? 'var(--color-emerald-600, #059669)' : 'var(--color-red-600, #dc2626)'};
`;

const signalStyles: Record<OrganizationSignal, ReturnType<typeof css>> = {
    healthy: css`
        background: var(--color-blue-50, #eff6ff);
        color: var(--color-blue-700, #1d4ed8);
    `,
    loss: css`
        background: var(--color-red-50, #fef2f2);
        color: var(--color-red-700, #b91c1c);
    `,
    leak: css`
        background: var(--color-amber-50, #fffbeb);
        color: var(--color-amber-700, #b45309);
    `,
};

export const SignalBadge = styled.span<{ $signal: OrganizationSignal }>`
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    ${({ $signal }) => signalStyles[$signal]}
`;

export const ListStack = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 8px;
`;

export const ListRow = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: 10px;
`;

export const ListPrimary = styled.span`
    font-weight: 600;
    color: var(--color-foreground, #111827);
`;

export const ListSecondary = styled.span`
    font-size: 12px;
    color: var(--color-muted-foreground, #6b7280);
    text-align: right;
`;

export const MoverDelta = styled.span<{ $tone: MetricHintTone }>`
    font-weight: 700;
    color: ${({ $tone }) => hintToneColor[$tone]};
`;

export { StatCard, StatLabel, StatValue, StatHint } from '@/components/MetricTile/styled';
